import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Copy, ExternalLink, Trash2, Eye, Globe2, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

function makeKioskUrl(token, preview = false) {
  const base = `${window.location.origin}/kiosk/${token}`;
  return preview ? `${base}?preview=1` : base;
}

export default function Experiences() {
  const { user, upgrade, pollUpgradeStatus, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [pollMsg, setPollMsg] = useState("");

  const load = () => api.get("/experiences").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  // On return from Stripe Checkout: ?session_id=... → poll status until paid or expired
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const sid = sp.get("session_id");
    const cancelled = sp.get("upgrade");
    if (cancelled === "cancelled") {
      toast.error("Upgrade cancelled.");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (!sid) return;
    let attempts = 0;
    const maxAttempts = 8;
    const interval = 2500;
    const tick = async () => {
      if (attempts === 0) {
        setUpgradeOpen(true);
        setPollMsg("Confirming your payment…");
      }
      if (attempts >= maxAttempts) {
        setPollMsg("Payment status check timed out. Refresh in a moment.");
        return;
      }
      attempts += 1;
      const r = await pollUpgradeStatus(sid);
      if (r.ok && r.payment_status === "paid") {
        setPollMsg("");
        setUpgradeOpen(false);
        toast.success("Upgraded! Your kiosk can now go public.");
        await refresh();
        window.history.replaceState({}, "", window.location.pathname);
        return;
      }
      if (r.ok && (r.status === "expired" || r.payment_status === "failed")) {
        setPollMsg("Payment did not complete.");
        return;
      }
      setTimeout(tick, interval);
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async () => {
    if (!name.trim()) {
      toast.error("Name required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/experiences", { name });
      setItems((prev) => [...prev, data]);
      toast.success("Experience created — preview it before publishing");
      setOpen(false);
      setName("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this experience?")) return;
    await api.delete(`/experiences/${id}`);
    setItems(items.filter((e) => e.id !== id));
  };

  const copy = async (token, preview) => {
    const url = makeKioskUrl(token, preview);
    let ok = false;
    try {
      // Modern API — fails in some iframes due to Permissions-Policy
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      // Fallback: hidden textarea + execCommand (works inside preview iframes)
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ok = document.execCommand && document.execCommand("copy");
        document.body.removeChild(ta);
      } catch { ok = false; }
    }
    if (ok) {
      toast.success(preview ? "Preview link copied" : "Public link copied");
    } else {
      // Last resort: show the URL so the user can copy manually
      toast.error("Copy blocked by browser. Long-press the link to copy.", { duration: 6000 });
      window.prompt("Copy this link:", url);
    }
  };

  const publish = async (id) => {
    if (!user?.is_paid) {
      setUpgradeOpen(true);
      return;
    }
    try {
      const { data } = await api.post(`/experiences/${id}/publish`);
      setItems((prev) => prev.map((e) => (e.id === id ? { ...e, ...data } : e)));
      toast.success("Published! Your kiosk URL is now live.");
    } catch (e) {
      if (e.response?.status === 402) {
        setUpgradeOpen(true);
        return;
      }
      toast.error(e.response?.data?.detail || "Failed to publish");
    }
  };

  const unpublish = async (id) => {
    try {
      await api.post(`/experiences/${id}/unpublish`);
      setItems((prev) => prev.map((e) => (e.id === id ? { ...e, is_published: false } : e)));
      toast.success("Unpublished");
    } catch {
      toast.error("Failed");
    }
  };

  const [planChoice, setPlanChoice] = useState("annual_100"); // "annual_100" | "monthly_10" | "undecided"
  const [leadForm, setLeadForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [submittingLead, setSubmittingLead] = useState(false);

  // Pre-fill from logged-in account when modal opens
  useEffect(() => {
    if (upgradeOpen && user && !leadForm.email) {
      setLeadForm((f) => ({
        ...f,
        email: user.email || "",
        company: user.brand_name || "",
      }));
    }
  }, [upgradeOpen, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitLead = async (e) => {
    e?.preventDefault?.();
    if (!leadForm.name.trim() || !leadForm.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmittingLead(true);
    try {
      await api.post("/leads", {
        name: leadForm.name.trim(),
        email: leadForm.email.trim(),
        phone: leadForm.phone.trim(),
        company: leadForm.company.trim(),
        plan_interest: planChoice,
        message: leadForm.message.trim(),
      });
      setLeadSubmitted(true);
      toast.success("Got it — we'll reach out within 24 hours.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setSubmittingLead(false);
    }
  };

  const closeUpgrade = () => {
    setUpgradeOpen(false);
    // Reset after dialog closes so it re-pre-fills if reopened
    setTimeout(() => {
      setLeadSubmitted(false);
      setLeadForm({ name: "", email: "", phone: "", company: "", message: "" });
    }, 300);
  };

  return (
    <>
      <PageHeader
        eyebrow="Deploy"
        title="Experiences"
        description="Build & preview your kiosk for free. Publish it to a public URL anytime — €10/mo or €100/yr."
      >
        <Button
          onClick={() => setOpen(true)}
          data-testid="create-experience-button"
          className="rounded-none bg-[#0F1210] hover:bg-black text-white h-11 px-5"
        >
          <Plus className="mr-2 w-4 h-4" /> New experience
        </Button>
      </PageHeader>

      {!user?.is_paid && (
        <div className="px-10 pt-6">
          <div
            className="border border-[#e5e5e5] bg-gradient-to-r from-[#f7f4ee] to-white p-6 flex items-center justify-between gap-6"
            data-testid="upgrade-banner"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[#0F1210] text-white flex items-center justify-center shrink-0">
                <Lock className="w-4 h-4" />
              </div>
              <div>
                <div className="label-overline">Sandbox mode</div>
                <div className="mt-1 font-display font-bold text-lg">
                  Preview is free. Publishing is €10/mo or €100/yr — unlimited.
                </div>
                <p className="text-sm text-[#52525b] mt-1">
                  Build your full experience, test the kiosk flow, and only pay when you&apos;re ready to put a live URL or QR code on the shop floor.
                </p>
              </div>
            </div>
            <Button
              onClick={() => setUpgradeOpen(true)}
              data-testid="open-upgrade-button"
              className="rounded-none bg-[#0F1210] hover:bg-black text-white h-11 px-5 shrink-0"
            >
              Upgrade · from €10/mo
            </Button>
          </div>
        </div>
      )}

      <div className="p-10 grid md:grid-cols-2 gap-6">
        {items.length === 0 && (
          <div
            className="md:col-span-2 bg-white border border-[#e5e5e5] p-16 text-center text-sm text-[#52525b]"
            data-testid="empty-experiences"
          >
            No experiences yet. Create one — it&apos;s free to build &amp; preview.
          </div>
        )}
        {items.map((e) => {
          const published = !!e.is_published;
          const previewUrl = makeKioskUrl(e.token, true);
          const publicUrl = makeKioskUrl(e.token, false);
          return (
            <div key={e.id} className="bg-white border border-[#e5e5e5] p-6" data-testid={`experience-card-${e.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="label-overline flex items-center gap-2">
                    {published ? (
                      <><Globe2 className="w-3 h-3 text-emerald-600" /> Live</>
                    ) : (
                      <><Eye className="w-3 h-3 text-[#a1a1aa]" /> Draft · preview only</>
                    )}
                  </div>
                  <div className="mt-2 font-display font-bold text-xl tracking-tight">{e.name}</div>
                </div>
                <button
                  onClick={() => remove(e.id)}
                  className="p-2 hover:bg-[#fafafa] border border-[#e5e5e5] text-[#e63946]"
                  data-testid={`delete-experience-${e.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Preview row — always available */}
              <div className="mt-6">
                <Label className="label-overline flex items-center gap-2"><Eye className="w-3 h-3" /> Preview (private)</Label>
                <div className="mt-2 flex items-stretch gap-2">
                  <Input
                    value={previewUrl}
                    readOnly
                    className="rounded-none border-[#e5e5e5] font-mono text-xs"
                    data-testid={`preview-input-${e.id}`}
                  />
                  <Button
                    variant="outline"
                    onClick={() => copy(e.token, true)}
                    className="rounded-none border-[#e5e5e5] h-10"
                    data-testid={`copy-preview-${e.id}`}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <a href={previewUrl} target="_blank" rel="noreferrer">
                    <Button variant="outline" className="rounded-none border-[#e5e5e5] h-10" data-testid={`open-preview-${e.id}`}>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                  </a>
                </div>
              </div>

              {/* Public row — only when published */}
              {published ? (
                <div className="mt-4">
                  <Label className="label-overline flex items-center gap-2 text-emerald-700"><Globe2 className="w-3 h-3" /> Public kiosk URL</Label>
                  <div className="mt-2 flex items-stretch gap-2">
                    <Input
                      value={publicUrl}
                      readOnly
                      className="rounded-none border-emerald-200 bg-emerald-50/30 font-mono text-xs"
                      data-testid={`public-input-${e.id}`}
                    />
                    <Button
                      variant="outline"
                      onClick={() => copy(e.token, false)}
                      className="rounded-none border-[#e5e5e5] h-10"
                      data-testid={`copy-public-${e.id}`}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <a href={publicUrl} target="_blank" rel="noreferrer">
                      <Button variant="outline" className="rounded-none border-[#e5e5e5] h-10" data-testid={`open-public-${e.id}`}>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </a>
                  </div>
                </div>
              ) : null}

              <div className="mt-6 flex items-center gap-3">
                {published ? (
                  <Button
                    variant="outline"
                    onClick={() => unpublish(e.id)}
                    className="rounded-none border-[#e5e5e5] h-10"
                    data-testid={`unpublish-${e.id}`}
                  >
                    Unpublish
                  </Button>
                ) : (
                  <Button
                    onClick={() => publish(e.id)}
                    data-testid={`publish-${e.id}`}
                    className="rounded-none bg-[#0F1210] hover:bg-black text-white h-10 px-5"
                  >
                    {user?.is_paid ? "Publish kiosk" : "Publish · from €10/mo"}
                  </Button>
                )}
                {published && (
                  <img
                    alt="qr"
                    className="border border-[#e5e5e5] p-2 bg-white ml-auto"
                    style={{ width: 96, height: 96 }}
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=0&data=${encodeURIComponent(publicUrl)}`}
                  />
                )}
              </div>

              <div className="mt-6 grid grid-cols-2 gap-px bg-[#e5e5e5] border border-[#e5e5e5]">
                <div className="bg-white p-4">
                  <div className="label-overline">Visits</div>
                  <div className="mt-2 font-display font-bold text-2xl tracking-tight">{e.visits || 0}</div>
                </div>
                <div className="bg-white p-4">
                  <div className="label-overline">Status</div>
                  <div className={`mt-2 font-display font-bold text-2xl tracking-tight ${published ? "text-emerald-600" : "text-[#a1a1aa]"}`}>
                    {published ? "Live" : "Draft"}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border border-[#e5e5e5]">
          <DialogHeader>
            <DialogTitle className="font-display font-bold tracking-tight text-2xl">New experience</DialogTitle>
            <DialogDescription>
              Build & preview for free. Publish to a public URL anytime.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="label-overline">Name</Label>
            <Input
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              placeholder="Flagship Store · NYC"
              className="mt-2 rounded-none border-[#e5e5e5]"
              data-testid="new-experience-name"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none border-[#e5e5e5] h-11 px-5">Cancel</Button>
            <Button
              onClick={create}
              disabled={busy}
              data-testid="confirm-create-experience"
              className="rounded-none bg-[#0F1210] hover:bg-black text-white h-11 px-5"
            >
              {busy ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request-access dialog (replaces direct payment) */}
      <Dialog open={upgradeOpen} onOpenChange={(v) => v ? setUpgradeOpen(true) : closeUpgrade()}>
        <DialogContent className="rounded-none border border-[#e5e5e5] max-w-lg">
          {!leadSubmitted ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display font-bold tracking-tight text-3xl flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-[#FF4D6D]" /> Request access to go live
                </DialogTitle>
                <DialogDescription className="text-base text-[#52525b]">
                  Keep playing in the sandbox for free. When you&apos;re ready to publish to a public URL, pick a plan below and we&apos;ll reach out within 24 hours to get you set up.
                </DialogDescription>
              </DialogHeader>

              {/* Plan toggle */}
              <div className="grid grid-cols-2 gap-3 mt-2">
                {[
                  { id: "monthly_10", price: "€10", per: "/ month", note: "Cancel any time" },
                  { id: "annual_100", price: "€100", per: "/ year",  note: "Save ~17% · €8.33/mo" },
                ].map((p) => {
                  const selected = planChoice === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPlanChoice(p.id)}
                      data-testid={`plan-${p.id}`}
                      className={`relative text-left rounded-none border p-4 transition-colors ${
                        selected
                          ? "border-[#FF4D6D] bg-[#FFE4E9] text-[#1B1340]"
                          : "border-[#e5e5e5] bg-white text-[#0a0a0a] hover:border-[#0a0a0a]"
                      }`}
                    >
                      {p.id === "annual_100" && (
                        <span className="absolute -top-2 right-3 text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 bg-[#FF4D6D] text-white">
                          Best value
                        </span>
                      )}
                      <div className="text-2xl font-display font-medium leading-none">
                        {p.price}
                        <span className="text-xs ml-1 font-mono text-[#71717a]">{p.per}</span>
                      </div>
                      <div className="text-xs mt-2 text-[#71717a]">{p.note}</div>
                    </button>
                  );
                })}
              </div>

              {/* Lead form */}
              <form onSubmit={submitLead} className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.18em] text-[#71717a] mb-1">Your name *</label>
                    <input
                      data-testid="lead-name"
                      type="text"
                      required
                      value={leadForm.name}
                      onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                      className="w-full border border-[#e5e5e5] rounded-none px-3 h-10 text-sm focus:outline-none focus:border-[#FF4D6D]"
                      placeholder="Jane Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.18em] text-[#71717a] mb-1">Brand</label>
                    <input
                      data-testid="lead-company"
                      type="text"
                      value={leadForm.company}
                      onChange={(e) => setLeadForm({ ...leadForm, company: e.target.value })}
                      className="w-full border border-[#e5e5e5] rounded-none px-3 h-10 text-sm focus:outline-none focus:border-[#FF4D6D]"
                      placeholder="Belle Vue"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.18em] text-[#71717a] mb-1">Email *</label>
                    <input
                      data-testid="lead-email"
                      type="email"
                      required
                      value={leadForm.email}
                      onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                      className="w-full border border-[#e5e5e5] rounded-none px-3 h-10 text-sm focus:outline-none focus:border-[#FF4D6D]"
                      placeholder="jane@bellevue.com"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.18em] text-[#71717a] mb-1">Phone (optional)</label>
                    <input
                      data-testid="lead-phone"
                      type="tel"
                      value={leadForm.phone}
                      onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                      className="w-full border border-[#e5e5e5] rounded-none px-3 h-10 text-sm focus:outline-none focus:border-[#FF4D6D]"
                      placeholder="+1 555 123 4567"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.18em] text-[#71717a] mb-1">Anything we should know? (optional)</label>
                  <textarea
                    data-testid="lead-message"
                    rows={3}
                    value={leadForm.message}
                    onChange={(e) => setLeadForm({ ...leadForm, message: e.target.value })}
                    className="w-full border border-[#e5e5e5] rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#FF4D6D] resize-none"
                    placeholder="Number of stores, integrations needed, timeline…"
                  />
                </div>
                <p className="text-xs text-[#a1a1aa]">
                  No payment now. You&apos;ll hear from us within 24h.
                </p>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={closeUpgrade} className="rounded-none border-[#e5e5e5] h-11 px-5">
                    Maybe later
                  </Button>
                  <Button
                    type="submit"
                    disabled={submittingLead}
                    data-testid="confirm-upgrade-button"
                    className="rounded-none bg-[#FF4D6D] hover:bg-[#E03A5A] text-white h-11 px-5"
                  >
                    {submittingLead ? "Sending…" : "Request access"}
                  </Button>
                </DialogFooter>
              </form>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display font-bold tracking-tight text-3xl flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-[#FF4D6D]" /> Thanks — we&apos;ll be in touch
                </DialogTitle>
                <DialogDescription className="text-base text-[#52525b]">
                  We received your request for the <span className="font-medium text-[#1B1340]">{planChoice === "monthly_10" ? "€10/month" : "€100/year"}</span> plan. We&apos;ll email <span className="font-medium text-[#1B1340]">{leadForm.email}</span> within 24 hours to get you live.
                </DialogDescription>
              </DialogHeader>
              <div className="bg-[#FFF6EB] border border-[#FFE4D6] p-4 text-sm text-[#3F2D5C]">
                <p className="font-medium mb-1">While you wait:</p>
                <ul className="space-y-1 list-disc list-inside text-[#5E6660]">
                  <li>Keep refining your products and questions</li>
                  <li>Share the preview link with your team</li>
                  <li>We&apos;ll send the public URL + QR once you&apos;re set up</li>
                </ul>
              </div>
              <DialogFooter>
                <Button onClick={closeUpgrade} data-testid="lead-done" className="rounded-none bg-[#FF4D6D] hover:bg-[#E03A5A] text-white h-11 px-5">
                  Got it
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
